import { useParams } from "react-router-dom";

import Markdown from "react-markdown";
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Grid2,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/PageTitle";
import { useAuth } from "../../shared/auth/AuthContext";
import { CreateMatch } from "../components/CreateMatch";
import { useEffect, useRef, useState } from "react";
import { MatchListCard } from "../components/MatchListCard";
import BoxCard from "../../shared/components/BoxCard";
import useGameAndVersion from "../../shared/hooks/useGameAndVersion";
import useUserId from "../../shared/hooks/useUserId";
import useIsSuperUser from "../../shared/hooks/useIsSuperUser";
import PeopleIcon from "@mui/icons-material/People";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LanguageIcon from "@mui/icons-material/Language";
import CodeIcon from "@mui/icons-material/Code";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { brand } from "../../shared/util/branding";

const botInstallUrl = import.meta.env.VITE_BOT_INSTALL_URL;
const botName =
  import.meta.env.VITE_BOT_NAME ||
  brand.name.toLowerCase().replace(/\s+/g, "-");

export default function GameSummary() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [uid] = useUserId();
  const isSuperUser = useIsSuperUser();
  const [creatingMatch, setCreatingMatch] = useState(false);
  const createMatchRef = useRef<HTMLDivElement>(null);

  // Bring the create-match form into view once it replaces the button: scroll just far enough to
  // show all of it, or to its top when it is taller than the window
  useEffect(() => {
    const el = createMatchRef.current;
    if (!creatingMatch || !el) return;
    const rect = el.getBoundingClientRect();
    const margin = 16;
    const viewport = window.innerHeight;
    let delta = 0;
    if (rect.height + margin * 2 > viewport || rect.top < margin) {
      delta = rect.top - margin;
    } else if (rect.bottom + margin > viewport) {
      delta = rect.bottom + margin - viewport;
    }
    if (delta !== 0) {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollBy({ top: delta, behavior: reduceMotion ? "auto" : "smooth" });
    }
  }, [creatingMatch]);
  const [copied, setCopied] = useState(false);
  const [discordOpen, setDiscordOpen] = useState(false);
  const { name, version } = useParams();

  const handleCreateMatch = () => {
    if (!user) {
      window.localStorage.setItem("postLoginUrl", document.location.href);
      navigate("/login");
      return;
    }
    setCreatingMatch(true);
  };

  if (!name) {
    throw "No name specified";
  }

  const { game, gameVersion, loading } = useGameAndVersion(name, version);

  if (loading || !game || !gameVersion) {
    return <Typography>Loading...</Typography>;
  }

  const demos = gameVersion.demos ?? [];
  const demoBaseUrl = `/player/game/${name}/demo${version ? `/${version}` : ""}`;

  return (
    <Box>
      {/* Hero section with screenshot */}
      {gameVersion.screenshot && (
        <Box
          sx={{
            position: "relative",
            mx: { xs: -2, sm: -3 },
            mt: -4,
            mb: 3,
            // height: 240,
            maxHeight: "50vh",
            overflow: "hidden",
            maxWidth: "100vw",
          }}
        >
          <Box
            component="img"
            src={gameVersion.screenshot}
            alt={`${gameVersion.name} screenshot`}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              maxWidth: "100%",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              background: `linear-gradient(transparent, ${theme.palette.background.default})`,
            }}
          />
        </Box>
      )}

      <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ mb: 3 }}>
        {gameVersion.boxArt && (
          <Box
            component="img"
            src={gameVersion.boxArt}
            alt={`${gameVersion.name} box art`}
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              objectFit: "cover",
              border: `2px solid rgba(255, 255, 255, 0.1)`,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              mt: gameVersion.screenshot ? -6 : 0,
              position: "relative",
              zIndex: 1,
            }}
          />
        )}
        <Box sx={{ flex: 1 }}>
          {game.visibility && game.visibility !== "public" && (
            <Chip
              label={
                game.visibility.charAt(0).toUpperCase() +
                game.visibility.slice(1)
              }
              size="small"
              color={
                game.visibility === "private"
                  ? "warning"
                  : game.visibility === "archived"
                    ? "error"
                    : "default"
              }
              variant="outlined"
            />
          )}
          <PageTitle title={gameVersion.name} />
          <Stack spacing={0.5} sx={{ mt: -2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PeopleIcon
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                {gameVersion.minPlayers === gameVersion.maxPlayers
                  ? `${gameVersion.minPlayers} players`
                  : `${gameVersion.minPlayers}-${gameVersion.maxPlayers} players`}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <Box ref={createMatchRef} sx={{ pb: 2 }}>
        {creatingMatch ? (
          <CreateMatch
            game={game}
            version={gameVersion}
            majorVersion={version ? gameVersion.major : undefined}
          />
        ) : (
          <Button
            variant="contained"
            onClick={handleCreateMatch}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              fontWeight: 600,
            }}
          >
            Create Match
          </Button>
        )}
      </Box>

      <Box>
        <Grid2 container spacing={2} direction="row-reverse">
          <Grid2 size={{ xs: 12, md: 4 }}>
            {demos.length > 0 && (
              <BoxCard title="Play Demo" sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {demos.some((d) => d.kind === "tutorial")
                    ? "Learn the rules with a guided tutorial, or get a feel for the game by playing a quick demo against the AI."
                    : "Get a feel for the game by playing a quick demo against the AI."}
                </Typography>
                <Stack spacing={1}>
                  {demos.map((demo) => (
                    <Button
                      key={demo.id}
                      variant="outlined"
                      size="small"
                      href={`${demoBaseUrl}?demo=${encodeURIComponent(demo.id)}`}
                      startIcon={
                        demo.kind === "tutorial" ? (
                          <SchoolIcon />
                        ) : (
                          <SportsEsportsIcon />
                        )
                      }
                    >
                      {demo.title}
                    </Button>
                  ))}
                </Stack>
              </BoxCard>
            )}
            {botInstallUrl && (
              <Card elevation={1} sx={{ mb: 2, p: 3 }}>
                <Box
                  onClick={() => setDiscordOpen(!discordOpen)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ fontWeight: "bold" }}>
                    Add to your server
                  </Typography>
                  <ExpandMoreIcon
                    sx={{
                      color: theme.palette.text.secondary,
                      transform: discordOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Collapse in={discordOpen}>
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Set up a library for this game in your Discord server.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      href={botInstallUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        background: "#5865F2",
                        "&:hover": { background: "#4752C4" },
                        alignSelf: "flex-start",
                      }}
                    >
                      Install Bot
                    </Button>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        background: brand.colors.footerBackground,
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        component="code"
                        sx={{
                          flex: 1,
                          fontSize: "0.75rem",
                          wordBreak: "break-all",
                        }}
                      >
                        {`/${botName} library thread_channel: game:${name}`}
                      </Box>
                      <Tooltip title={copied ? "Copied!" : "Copy"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(
                              `/${botName} library thread_channel: game:${name}`,
                            );
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                          sx={{
                            flexShrink: 0,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Collapse>
              </Card>
            )}
            <MatchListCard
              label="Pending Games"
              status="pending"
              gameId={name}
              hideGameLink
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Markdown>{gameVersion.description}</Markdown>

            {(gameVersion.designer || gameVersion.artist) && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  Credits
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {gameVersion.designer && (
                    <Chip
                      label={`Designer: ${gameVersion.designer}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {gameVersion.artist && (
                    <Chip
                      label={`Artist: ${gameVersion.artist}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}

            {(gameVersion.tutorialVideoUrl ||
              gameVersion.rulesPdfUrl ||
              gameVersion.purchaseUrl ||
              gameVersion.publisherUrl ||
              gameVersion.repositoryUrl) && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  Links
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {gameVersion.tutorialVideoUrl && (
                    <Button
                      component={Link}
                      href={gameVersion.tutorialVideoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      startIcon={<OndemandVideoIcon />}
                    >
                      Tutorial Video
                    </Button>
                  )}
                  {gameVersion.rulesPdfUrl && (
                    <Button
                      component={Link}
                      href={gameVersion.rulesPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      startIcon={<MenuBookIcon />}
                    >
                      Rules PDF
                    </Button>
                  )}
                  {gameVersion.purchaseUrl && (
                    <Button
                      component={Link}
                      href={gameVersion.purchaseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      startIcon={<ShoppingCartIcon />}
                    >
                      Purchase Physical Copy
                    </Button>
                  )}
                  {gameVersion.publisherUrl && (
                    <Button
                      component={Link}
                      href={gameVersion.publisherUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      startIcon={<LanguageIcon />}
                    >
                      {gameVersion.publisher || "Publisher Website"}
                    </Button>
                  )}
                  {gameVersion.repositoryUrl && (
                    <Button
                      component={Link}
                      href={gameVersion.repositoryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      startIcon={<CodeIcon />}
                    >
                      Source Code
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            {gameVersion.publisher && !gameVersion.publisherUrl && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  Publisher
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {gameVersion.publisher}
                </Typography>
              </Box>
            )}

            {gameVersion.licenseInformation && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  License
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {gameVersion.licenseInformation}
                </Typography>
              </Box>
            )}

            {gameVersion.implementationNotes && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  Implementation Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {gameVersion.implementationNotes}
                </Typography>
              </Box>
            )}
          </Grid2>

          {(game.ownerUserId === uid || isSuperUser) && (
            <Grid2 size={{ xs: 12 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/game/${name}/edit`)}
                sx={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: theme.palette.secondary.light,
                    background: "rgba(140, 0, 255, 0.1)",
                  },
                }}
              >
                Admin Edit
              </Button>
            </Grid2>
          )}
        </Grid2>
      </Box>

      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, mt: 4, display: "block" }}
      >
        Version {gameVersion.version}
      </Typography>
    </Box>
  );
}
